import { useState } from "react";
import { Badge } from "../../../client/components/ui/badge";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { pnlColor, winRateColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

type SortKey = "tag" | "trades" | "winRate" | "totalPnl" | "avgPnl";
type SortDir = "asc" | "desc";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmtMoney(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)} $`;
}

export default function TagStatsWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, t } = useDashboardData();
  const [sortKey, setSortKey] = useState<SortKey>("totalPnl");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  if (!analyticsData || analyticsData.tagStats.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader label={t.dashboard.widgets.tagStats} isDark={isDark} compact />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noTags} />
        </CardContent>
      </Card>
    );
  }

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "tag" ? "asc" : "desc");
    }
  };

  const sorted = [...analyticsData.tagStats].sort((a, b) => {
    let cmp: number;
    if (sortKey === "tag") {
      cmp = a.tag.localeCompare(b.tag);
    } else {
      cmp = (a[sortKey] as number) - (b[sortKey] as number);
    }
    return sortDir === "asc" ? cmp : -cmp;
  });

  const arrow = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const headerClass = (align: "left" | "right") =>
    `${align === "left" ? "text-left" : "text-right"} p-2 text-xs font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors`;

  const tableColumns = [
    { key: "tag", label: t.analytics.tagLabel, align: "left" as const },
    { key: "trades", label: t.analytics.numberOfTrades, align: "right" as const },
    { key: "pnl", label: t.analytics.pnlLabel, align: "right" as const },
    { key: "winRate", label: t.analytics.winRateLabel, align: "right" as const },
  ];
  const tableRows = sorted.map((stat) => ({
    tag: (
      <Badge variant="secondary" className="text-xs font-normal">
        {stat.tag}
      </Badge>
    ),
    trades: stat.trades,
    pnl: (
      <span className={stat.totalPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {fmtMoney(stat.totalPnl)}
      </span>
    ),
    winRate: (
      <span style={{ color: winRateColor(stat.winRate) }}>{stat.winRate.toFixed(1)}%</span>
    ),
  }));

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.dashboard.widgets.tagStats}
        title={t.analytics.tagStatsTitle}
        isDark={isDark}
        compact
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="flex-1 overflow-auto px-5 pb-5 pt-1">
        {viewMode === "table" ? (
          <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noTags} />
        ) : (
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b">
              <th className={headerClass("left")} onClick={() => handleSort("tag")}>
                {t.analytics.tagStatsTitle}
                {arrow("tag")}
              </th>
              <th className={headerClass("right")} onClick={() => handleSort("trades")}>
                {t.analytics.trades}
                {arrow("trades")}
              </th>
              <th className={headerClass("right")} onClick={() => handleSort("winRate")}>
                {t.analytics.winRate}
                {arrow("winRate")}
              </th>
              <th className={headerClass("right")} onClick={() => handleSort("totalPnl")}>
                {t.analytics.totalPnl}
                {arrow("totalPnl")}
              </th>
              <th className={headerClass("right")} onClick={() => handleSort("avgPnl")}>
                {t.analytics.averagePnl}
                {arrow("avgPnl")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((stat) => (
              <tr
                key={stat.tag}
                className="border-b last:border-0 hover:bg-muted/30 transition-colors"
              >
                <td className="p-2 font-medium">
                  <Badge variant="secondary" className="text-xs font-normal">
                    {stat.tag}
                  </Badge>
                </td>
                <td className="p-2 text-right tabular-nums">{stat.trades}</td>
                <td className="p-2 text-right tabular-nums">
                  <span style={{ color: winRateColor(stat.winRate) }}>
                    {stat.winRate.toFixed(1)}%
                  </span>
                </td>
                <td
                  className="p-2 text-right font-medium font-mono tabular-nums"
                  style={{ color: pnlColor(stat.totalPnl) }}
                >
                  {fmtMoney(stat.totalPnl)}
                </td>
                <td
                  className="p-2 text-right font-mono tabular-nums"
                  style={{ color: pnlColor(stat.avgPnl) }}
                >
                  {fmtMoney(stat.avgPnl)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </CardContent>
    </Card>
  );
}
